import { IntelHexResult } from './IntelHex';

/**
 * Defines the EFM8 derivatives.
 */
const efm8Deriv = {
	EFM8UB10F16G_QFN28: 0x41,
	EFM8UB10F16G_QFN20: 0x43,
	EFM8UB11F16G_QSOP24: 0x45,
	EFM8UB10F8G_QFN20: 0x49,
	EFM8UB20F32G: 0x52,
};

const SECTOR_SIZE_FWARE_EFM8 = 512;

/**
 * Represents the start and end erase sectors for a chip derivative.
 */
export type EraseSecorCount = {
	start: number;
	end: number;
};

/**
 * Represents a firmware block.
 */
export type FirmwareBlock = {
	block: boolean;
	start_address?: number;
	end_address?: number;
	length?: number;
	data?: Uint8Array;
};

/**
 * Gets the erase sector count for a given chip derivative.
 * @param chipDerivative The chip derivative.
 * @returns The start and end erase sectors.
 * @throws Error if the chip derivative is invalid.
 */
export function getEraseSectorCount(chipDerivative: number): EraseSecorCount {
	let topAddr = 0;

	switch (chipDerivative) {
		case efm8Deriv.EFM8UB10F16G_QFN28:
			topAddr = 0x37ff;
			break;
		case efm8Deriv.EFM8UB10F8G_QFN20:
			topAddr = 0x17ff;
			break;
		case efm8Deriv.EFM8UB10F16G_QFN20:
			topAddr = 0x37ff;
			break;
		case efm8Deriv.EFM8UB11F16G_QSOP24:
			topAddr = 0x37ff;
			break;
		case efm8Deriv.EFM8UB20F32G:
			topAddr = 0x6dff;
			break;
		default:
			break;
	}

	if (!topAddr) {
		throw new Error(
			'Invalid device identification. Cannot calculate erase sectors, Derivative not valid: ' + chipDerivative
		);
	}

	const numberSectors = (topAddr + 1) / SECTOR_SIZE_FWARE_EFM8;
	return { start: 0, end: numberSectors };
}

/**
 * Scrambles the bytes of a Buffer by performing a bitwise XOR operation with 0x15.
 *
 * @param datab - The Buffer to be scrambled.
 * @returns The scrambled Buffer.
 */
export function scrambledEggs(datab: Uint8Array): Uint8Array {
	for (let i = 0; i < datab.length; i++) {
		datab[i] = datab[i] ^ 0x15;
	}

	return datab;
}

/**
 * Locates a block of data within a buffer and returns information about the block.
 * @param buffer The buffer to search within.
 * @param startAddress The starting address of the search range.
 * @param endAddress The ending address of the search range.
 * @returns An object containing information about the located block, including its start address, end address, length, and data.
 * If no block is found, the object will have a `block` property set to `false`.
 */
export function flashLocateBlock(buffer: Uint8Array, startAddress: number, endAddress: number): FirmwareBlock {
	let startAa = -1;
	let i = startAddress;

	do {
		if (buffer[i] !== 0xff) {
			startAa = i;
		}

		i++;
	} while (i <= endAddress && startAa === -1);

	if (startAa === -1) {
		return { block: false };
	}

	let endA = -1;

	do {
		if (buffer[i] === 0xff) {
			endA = i;
		}

		i++;
	} while (i <= endAddress && endA === -1);

	let us: any = buffer.slice(startAa, endA);

	us = scrambledEggs(us);
	return { block: true, start_address: startAa, end_address: endA, length: endA - startAa, data: us };
}

/**
 * Calculates the firmware blocks based on the provided IntelHexResult and chipDerivative.
 *
 * @param buffer - The IntelHexResult containing the firmware data.
 * @param chipDerivative - The chip derivative identifier.
 * @returns An array of firmware blocks.
 * @throws Error if the device identification is invalid.
 */
export function calculateblocks(buffer: IntelHexResult, chipType: number) {
	let topAddr = 0;

	switch (chipType) {
		case efm8Deriv.EFM8UB10F16G_QFN28:
			topAddr = 0x37ff;
			break;
		case efm8Deriv.EFM8UB10F8G_QFN20:
			topAddr = 0x17ff;
			break;
		case efm8Deriv.EFM8UB10F16G_QFN20:
			topAddr = 0x37ff;
			break;
		case efm8Deriv.EFM8UB11F16G_QSOP24:
			topAddr = 0x37ff;
			break;
		case efm8Deriv.EFM8UB20F32G:
			topAddr = 0x6dff;
			break;
		default:
			break;
	}

	if (!topAddr) {
		throw new Error("Invalid device identification. Can't calculate firmware blocks. Chip type not valid: " + chipType);
	}

	const blocklist = [];
	let index = 0;
	let finished = false;
	let failsafe = 0;

	while (finished === false) {
		if(failsafe > 10000) {
			console.error("Hit Firmware Failsafe");
			return null;
		}
		// Make sure no addresses after top address
		const bl = flashLocateBlock(buffer.data, index, topAddr + 1);

		if (bl.block) {
			index = bl.end_address || 0;
			blocklist.push(bl);
		} else {
			finished = true;
		}
		failsafe++;
	}

	return {list: blocklist, endAddress: index};
}
